import { useState } from 'react';
import { useMutation, useQuery } from 'urql';
import Field from '@/components/common/Field.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import exportCsv from '@/utils/exportCsv.js';
import {
  ASIGNACIONES_TURNOS_LIST,
  ASIGNACION_TURNO_DELETE,
} from '@/graphql/rrhh/turnos.gql.js';

const INITIAL_FILTERS = {
  empleado: '',
  turno: '',
  sucursal: '',
  estado: '',
  desde: '',
  hasta: '',
};

const INITIAL_PAGINATION = {
  page: 1,
  perPage: 10,
};

const ESTADOS = [
  { label: 'Todos', value: '' },
  { label: 'Activa', value: 'Activa' },
  { label: 'Pausada', value: 'Pausada' },
  { label: 'Finalizada', value: 'Finalizada' },
];

const ORDENES = [
  { label: 'Más recientes', value: 'desde:DESC' },
  { label: 'Empleado', value: 'empleadoNombre:ASC' },
  { label: 'Turno', value: 'turnoNombre:ASC' },
];

function mapFilters(filters) {
  return {
    empleadoId: filters.empleado ? Number(filters.empleado) : null,
    turnoId: filters.turno ? Number(filters.turno) : null,
    sucursalId: filters.sucursal ? Number(filters.sucursal) : null,
    estado: filters.estado || null,
    desde: filters.desde || null,
    hasta: filters.hasta || null,
  };
}

function getEstadoClass(estado) {
  switch (estado) {
    case 'Activa':
      return 'bg-[rgba(var(--brand),0.18)] text-[rgb(var(--brand-700))]';
    case 'Pausada':
      return 'bg-[rgba(var(--muted),0.18)] text-[rgba(var(--muted),0.95)]';
    case 'Finalizada':
      return 'bg-[rgba(var(--fg),0.08)] text-[rgb(var(--fg))]';
    default:
      return 'bg-[rgba(var(--muted),0.15)] text-[rgba(var(--muted),0.95)]';
  }
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ListadoAsignacionesTurno({ active, onCreate, onEdit }) {
  const toast = useToast();
  const [filterForm, setFilterForm] = useState(INITIAL_FILTERS);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [order, setOrder] = useState({ field: 'desde', direction: 'DESC' });

  const [{ data, fetching, error }] = useQuery({
    query: ASIGNACIONES_TURNOS_LIST,
    variables: {
      filters: mapFilters(filters),
      pagination,
      order,
    },
    pause: !active,
  });

  const [, deleteAsignacion] = useMutation(ASIGNACION_TURNO_DELETE);

  const asignaciones = data?.asignacionesTurno?.items ?? [];
  const pageInfo = data?.asignacionesTurno?.pageInfo ?? { ...pagination, totalItems: asignaciones.length, totalPages: 1 };
  const total = data?.asignacionesTurno?.total ?? asignaciones.length;

  const handleSubmitFilters = (event) => {
    event.preventDefault();
    setFilters(filterForm);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleOrderChange = (event) => {
    const [field, direction] = event.target.value.split(':');
    setOrder({ field, direction });
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('¿Eliminar la asignación seleccionada?');
    if (!confirmed) return;
    const result = await deleteAsignacion({ id });
    if (result.error) {
      toast.error(result.error?.message || 'No pudimos eliminar la asignación.');
      return;
    }
    toast.success('Asignación eliminada.');
  };

  const handleExport = () => {
    if (!asignaciones.length) {
      toast.info('No hay asignaciones para exportar.');
      return;
    }
    exportCsv({
      filename: 'asignaciones-turnos.csv',
      columns: [
        { label: 'ID', key: 'id' },
        { label: 'Empleado', key: 'empleadoNombre' },
        { label: 'Turno', key: 'turnoNombre' },
        { label: 'Desde', key: 'desde' },
        { label: 'Hasta', key: 'hasta' },
        { label: 'Estado', key: 'estado' },
        { label: 'Sucursal', key: 'sucursalNombre' },
      ],
      rows: asignaciones,
    });
    toast.success('Exportamos el CSV con las asignaciones.');
  };

  if (!active) {
    return null;
  }

  const isEmpty = !fetching && asignaciones.length === 0;

  return (
    <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.95)] p-6 shadow-[0_8px_30px_rgba(var(--shadow),0.15)]">
      <form onSubmit={handleSubmitFilters} className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-3">
          <Field label="Empleado" htmlFor="empleado" hint="ID colaborador">
            <input
              id="empleado"
              value={filterForm.empleado}
              onChange={event => setFilterForm(prev => ({ ...prev, empleado: event.target.value }))}
              placeholder="Ej: 1001"
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
        </div>
        <div className="col-span-12 md:col-span-3">
          <Field label="Turno" htmlFor="turno" hint="ID turno">
            <input
              id="turno"
              value={filterForm.turno}
              onChange={event => setFilterForm(prev => ({ ...prev, turno: event.target.value }))}
              placeholder="Ej: 3"
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
        </div>
        <div className="col-span-12 md:col-span-3">
          <Field label="Sucursal" htmlFor="sucursal">
            <input
              id="sucursal"
              value={filterForm.sucursal}
              onChange={event => setFilterForm(prev => ({ ...prev, sucursal: event.target.value }))}
              placeholder="ID sucursal"
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
        </div>
        <div className="col-span-12 sm:col-span-6 md:col-span-3">
          <Field label="Estado" htmlFor="estado">
            <select
              id="estado"
              value={filterForm.estado}
              onChange={event => setFilterForm(prev => ({ ...prev, estado: event.target.value }))}
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              {ESTADOS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="col-span-12 sm:col-span-6 md:col-span-3">
          <Field label="Desde" htmlFor="desde">
            <input
              id="desde"
              type="date"
              value={filterForm.desde}
              onChange={event => setFilterForm(prev => ({ ...prev, desde: event.target.value }))}
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
        </div>
        <div className="col-span-12 sm:col-span-6 md:col-span-3">
          <Field label="Hasta" htmlFor="hasta">
            <input
              id="hasta"
              type="date"
              value={filterForm.hasta}
              onChange={event => setFilterForm(prev => ({ ...prev, hasta: event.target.value }))}
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
        </div>
        <div className="col-span-12 sm:col-span-6 md:col-span-3">
          <Field label="Orden" htmlFor="order">
            <select
              id="order"
              value={`${order.field}:${order.direction}`}
              onChange={handleOrderChange}
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              {ORDENES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="col-span-12 flex flex-wrap items-end justify-between gap-3">
          <div className="flex gap-2">
            <button
              type="submit"
              className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] hover:bg-[rgb(var(--brand-700))]"
            >
              Aplicar filtros
            </button>
            <button
              type="button"
              onClick={() => {
                setFilterForm(INITIAL_FILTERS);
                setFilters(INITIAL_FILTERS);
                setPagination(INITIAL_PAGINATION);
              }}
              className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              Limpiar
            </button>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleExport}
              className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              Exportar CSV
            </button>
            <button
              type="button"
              onClick={onCreate}
              className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] hover:bg-[rgb(var(--brand-700))]"
            >
              Nueva asignación
            </button>
          </div>
        </div>
      </form>

      <div className="mt-8 overflow-x-auto">
        <table className="min-w-full table-auto border-separate border-spacing-y-2">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[rgba(var(--muted),0.9)]">
              <th className="px-3 py-2">Empleado</th>
              <th className="px-3 py-2">Turno</th>
              <th className="px-3 py-2">Desde</th>
              <th className="px-3 py-2">Hasta</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2">Sucursal</th>
              <th className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {fetching ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-sm text-[rgba(var(--muted),0.9)]">
                  Cargando asignaciones...
                </td>
              </tr>
            ) : null}

            {error ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-sm text-[rgba(var(--muted),0.9)]">
                  No pudimos cargar las asignaciones.
                </td>
              </tr>
            ) : null}

            {isEmpty ? (
              <tr>
                <td colSpan={7} className="px-3 py-6">
                  <EmptyState
                    title="Sin asignaciones"
                    description="Creá una asignación para vincular personas con turnos activos."
                  />
                </td>
              </tr>
            ) : null}

            {asignaciones.map(item => (
              <tr key={item.id} className="rounded-2xl bg-[rgba(var(--subpanel),0.35)] text-sm text-[rgb(var(--fg))] shadow-[0_4px_16px_rgba(var(--shadow),0.06)]">
                <td className="rounded-l-2xl px-3 py-3 align-top">
                  <div className="flex flex-col">
                    <span className="font-semibold">{item.empleadoNombre}</span>
                    <span className="text-xs text-[rgba(var(--muted),0.9)]">ID {item.empleadoId}</span>
                  </div>
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="flex flex-col">
                    <span className="font-semibold">{item.turnoNombre}</span>
                    <span className="text-xs text-[rgba(var(--muted),0.9)]">ID {item.turnoId}</span>
                  </div>
                </td>
                <td className="px-3 py-3 align-top">{formatDate(item.desde)}</td>
                <td className="px-3 py-3 align-top">{item.hasta ? formatDate(item.hasta) : 'Vigente'}</td>
                <td className="px-3 py-3 align-top">
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getEstadoClass(item.estado)}`}>
                    {item.estado}
                  </span>
                </td>
                <td className="px-3 py-3 align-top">{item.sucursalNombre || item.sucursalId || '—'}</td>
                <td className="rounded-r-2xl px-3 py-3 align-top">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit?.(item.id)}
                      className="rounded-lg border border-[rgba(var(--border),0.4)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--panel),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                      className="rounded-lg bg-[rgba(var(--fg),0.08)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-[rgba(var(--muted),0.9)]">
          Mostrando {asignaciones.length} de {total} asignaciones
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, (prev.page || 1) - 1) }))}
            disabled={(pageInfo.page || 1) <= 1}
            className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-1.5 text-sm font-medium text-[rgba(var(--muted),0.95)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
          >
            Anterior
          </button>
          <span className="text-sm font-medium text-[rgb(var(--fg))]">
            Página {pageInfo.page || 1} de {pageInfo.totalPages || 1}
          </span>
          <button
            type="button"
            onClick={() => setPagination(prev => ({ ...prev, page: (prev.page || 1) + 1 }))}
            disabled={(pageInfo.page || 1) >= (pageInfo.totalPages || 1)}
            className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-1.5 text-sm font-medium text-[rgba(var(--muted),0.95)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
}
